import React, { lazy } from "react";

// A tab that survives a deploy still holds the old index.html, whose lazy
// route chunks no longer exist on the server, so navigating throws a
// ChunkLoadError. Reloading the same URL picks up the new index.html and
// lands the user where they were headed. This boundary does that once.
//
// Only chunk-load failures are handled here. Anything else is re-thrown so the
// root <ErrorBoundary> still reports real render crashes.

const GUARD_KEY = "gumi_chunk_reload";

const CHUNK_ERROR_PATTERNS = [
  /Loading chunk [^\s]+ failed/i,
  /Loading CSS chunk [^\s]+ failed/i,
  /dynamically imported module/i, // Chrome / Firefox native import()
  /Importing a module script failed/i, // Safari
];

export const isChunkLoadError = (error) =>
  !!error &&
  (error.name === "ChunkLoadError" ||
    CHUNK_ERROR_PATTERNS.some((re) => re.test(String(error.message || ""))));

// True when this call is the one allowed reload. If sessionStorage is
// unavailable we can't guard against a loop, so we refuse to reload at all.
const claimReload = () => {
  try {
    if (sessionStorage.getItem(GUARD_KEY)) return false;
    sessionStorage.setItem(GUARD_KEY, "1");
    return true;
  } catch {
    return false;
  }
};

const clearGuard = () => {
  try {
    sessionStorage.removeItem(GUARD_KEY);
  } catch {
    // ignore
  }
};

// React.lazy for a route, clearing the reload guard once its chunk has actually
// loaded. That, and not anything about rendering, is the signal that a reload
// fixed the stale-index.html problem. (Tying it to a render is wrong: the auth
// gate renders a spinner before the lazy page is ever requested, so a render-
// time reset would wipe the flag before the failing fetch and loop forever.)
export const lazyRoute = (loader) =>
  lazy(() =>
    loader().then((mod) => {
      clearGuard();
      return mod;
    })
  );

export default class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, giveUp: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    if (!isChunkLoadError(error)) return;
    if (claimReload()) {
      window.location.reload();
    } else {
      // Already reloaded once and it still fails: stop, and let the root
      // ErrorBoundary show its message instead of looping.
      this.setState({ giveUp: true });
    }
  }

  render() {
    const { error, giveUp } = this.state;
    if (!error) return this.props.children;
    if (!isChunkLoadError(error) || giveUp) throw error;
    return null; // reload in flight
  }
}
